import { memo } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { ADMISSION_CODE_VALUES, type AdmissionCode } from "@shared/types/rejections.ts";
import { LoadingState } from "../../../../../components/primitives/feedback/LoadingState";
import {
  DataSequence,
  type DataSequenceColumn,
} from "../../../../../components/primitives/data-display/DataSequence";
import { CodeBlock } from "../../../../../components/primitives/data-display/CodeBlock";
import { DocumentLink } from "../../../../../components/domain/data-display/DocumentLink";
import { Dialog } from "../../../../../components/primitives/feedback/Dialog";
import { useOpenState } from "../../../../../hooks/useOpenState";
import { PubmedQueryText } from "../../../../../components/domain/data-display/PubmedQueryText";
import { PubmedIngestOutcomeBadge } from "../../../../../components/domain/data-display/PubmedIngestOutcomeBadge";
import { RequireRole } from "../../../../../components/gate/RequireRole";
import { RejectionFindingSummary } from "../../../../../components/domain/data-display/RejectionFindingSummary";
import { DocumentDetailDialog } from "../../../Library/Documents/DocumentDetailDialog";
import { PUBMED_INGEST_TAB_ORDER, PUBMED_INGEST_TABS } from "./pubmed_ingest_tabs";
import {
  documentIdOf,
  type PubmedIngestRow,
  type UsePubmedIngestPanelResult,
} from "./usePubmedIngestPanel";
import { getBatchJobItemRowId } from "../../panel/batch_job_item_rows";

/** Why a needs_review article is waiting for a person, in words. */
function reviewReasonText(result: NonNullable<PubmedIngestRow["result"]>): string {
  if (result.reviewReason === "admission_unclear") {
    return "The admission gate could not decide whether this paper belongs in the corpus.";
  }
  const fields = result.blockingFields?.join(", ");
  return fields
    ? `Not confident enough to ingest automatically: ${fields}.`
    : "Not confident enough to ingest automatically.";
}

function RowReason({ row }: { row: PubmedIngestRow }) {
  const { result, item } = row;
  if (!result) {
    return item.error ? (
      <Typography variant="caption" color="error">
        {item.error}
      </Typography>
    ) : (
      <>—</>
    );
  }
  if (result.rejection) {
    return (
      <RejectionFindingSummary code={result.rejection.code} message={result.rejection.message} />
    );
  }
  if (result.outcome === "needs_review") {
    return (
      <Typography variant="caption" color="text.secondary">
        {reviewReasonText(result)}
      </Typography>
    );
  }
  return <>—</>;
}

/** Draws a query with Entrez syntax colouring. */
function renderPubmedQuery(query: string) {
  return <PubmedQueryText query={query} />;
}

interface QueryDialogButtonProps {
  query: string;
}

/** A button that opens the job's full query in a dialog, where it can be read and copied. */
const QueryDialogButton = memo(function QueryDialogButton({ query }: QueryDialogButtonProps) {
  const dialog = useOpenState();
  return (
    <>
      <Button size="small" onClick={dialog.setOpen}>
        View query
      </Button>
      <Dialog open={dialog.open} onClose={dialog.setClose} title="PubMed Central query">
        <CodeBlock code={query} renderCode={renderPubmedQuery} />
      </Dialog>
    </>
  );
});

export interface PubmedIngestPanelBodyProps {
  panel: UsePubmedIngestPanelResult;
}

/** Presentational pubmed_ingest results panel: one tab per outcome, each a paginated list of the
 *  articles that landed there. Read-only — what needs a person already waits in the annotation
 *  queue — except that a row whose article is in the corpus opens its document details. */
export function PubmedIngestPanelBody({ panel }: PubmedIngestPanelBodyProps) {
  const columns: DataSequenceColumn<PubmedIngestRow>[] = [
    {
      key: "subject",
      header: "Article",
      width: 320,
      cardRole: "primary",
      cell: (row) => <DocumentLink title={row.item.subjectTitle} url={row.item.subjectUrl} />,
    },
    {
      key: "outcome",
      header: "Outcome",
      cell: (row) =>
        row.result ? (
          <PubmedIngestOutcomeBadge outcome={row.result.outcome} />
        ) : (
          <Typography variant="caption" color="text.secondary">
            {row.item.status === "failed" ? "Failed" : "…"}
          </Typography>
        ),
    },
    {
      key: "reason",
      header: "Details",
      cell: (row) => <RowReason row={row} />,
    },
    {
      key: "actions",
      header: "",
      cardRole: "action",
      cell: (row) => (
        <Stack direction="row" gap={1}>
          {documentIdOf(row) && (
            <Button size="small" onClick={() => panel.detail.open(row)}>
              Details
            </Button>
          )}
          {row.item.status === "failed" && (
            <RequireRole role="platform.admin">
              <Button
                size="small"
                disabled={panel.retry.loading}
                onClick={() => panel.retry.handle(row)}
              >
                {panel.retry.loading && panel.retry.retryingId === row.item.id
                  ? "Retrying…"
                  : "Retry"}
              </Button>
            </RequireRole>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Stack gap={2}>
      <Stack direction="row" gap={1} sx={{ alignItems: "center" }}>
        <Typography variant="caption" color="text.secondary">
          Up to {panel.params.maxResults} articles
        </Typography>
        <QueryDialogButton query={panel.params.query} />
      </Stack>

      <Tabs
        value={panel.tabs.activeTab}
        onChange={(_e, v: string) => panel.tabs.setActiveTab(v as typeof panel.tabs.activeTab)}
        sx={{ borderBottom: 1, borderColor: "divider" }}
        variant="scrollable"
      >
        {PUBMED_INGEST_TAB_ORDER.map((key) => (
          <Tab key={key} label={PUBMED_INGEST_TABS[key].label} value={key} />
        ))}
      </Tabs>

      {panel.tabs.tab.rejectionFilterable && (
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="pubmed-ingest-rejection-code-label">Rejection reason</InputLabel>
          <Select
            labelId="pubmed-ingest-rejection-code-label"
            label="Rejection reason"
            value={panel.filters.rejectionCode ?? ""}
            onChange={(e) =>
              panel.filters.setRejectionCode((e.target.value as AdmissionCode) || null)
            }
          >
            <MenuItem value="">All reasons</MenuItem>
            {ADMISSION_CODE_VALUES.map((code) => (
              <MenuItem key={code} value={code}>
                {code}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {panel.list.error && <Alert severity="error">{panel.list.error}</Alert>}
      {panel.retry.error && <Alert severity="error">{panel.retry.error}</Alert>}

      {panel.list.loading ? (
        <LoadingState />
      ) : (
        <DataSequence
          columns={columns}
          rows={panel.list.rows}
          getRowId={getBatchJobItemRowId}
          emptyMessage="No articles in this list."
          pagination={panel.list.pagination}
        />
      )}

      <DocumentDetailDialog item={panel.detail.target} onClose={panel.detail.close} />
    </Stack>
  );
}
